import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { systemMonitoringService } from '../../../services/systemMonitoringService';

const ConfigurationAudit = () => {
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAuditTrail();

    // Subscribe to real-time audit trail updates
    const subscription = systemMonitoringService?.subscribeToAuditTrail((newEntry) => {
      setAuditLog(prev => [newEntry, ...prev]);
    });

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, []);

  const loadAuditTrail = async () => {
    try {
      setLoading(true);
      setError('');
      const entries = await systemMonitoringService?.getAuditTrail({ limit: 50 });
      setAuditLog(entries);
    } catch (err) {
      console.error('Error loading audit trail:', err);
      setError(err?.message || 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const iconMap = {
      'invite_code_enabled': 'CheckCircle',
      'invite_code_disabled': 'XCircle',
      'invite_code_updated': 'RefreshCw',
      'logo_uploaded': 'Upload',
      'logo_updated': 'RefreshCw',
      'logo_deleted': 'Trash2',
      'hero_image_added': 'Image',
      'hero_image_updated': 'RefreshCw',
      'hero_image_deleted': 'Trash2',
      'founder_bio_updated': 'Edit',
      'play_added': 'Plus',
      'play_updated': 'Edit',
      'play_deleted': 'Trash2',
      'settings_changed': 'Settings',
      'password_changed': 'Lock',
      'backup_created': 'Save',
      'system_maintenance': 'Tool'
    };
    return iconMap?.[action] || 'Activity';
  };

  const getActionColor = (action) => {
    const colorMap = {
      'invite_code_enabled': 'text-success',
      'invite_code_disabled': 'text-warning',
      'invite_code_updated': 'text-accent',
      'logo_uploaded': 'text-accent',
      'logo_updated': 'text-accent',
      'logo_deleted': 'text-error',
      'hero_image_added': 'text-success',
      'hero_image_updated': 'text-accent',
      'hero_image_deleted': 'text-error',
      'founder_bio_updated': 'text-accent',
      'play_added': 'text-success',
      'play_updated': 'text-accent',
      'play_deleted': 'text-error',
      'settings_changed': 'text-foreground',
      'password_changed': 'text-warning',
      'backup_created': 'text-success',
      'system_maintenance': 'text-warning'
    };
    return colorMap?.[action] || 'text-muted-foreground';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground mb-1">
              Configuration Audit Trail
            </h3>
            <p className="text-sm text-muted-foreground">
              Track all configuration changes and administrative actions
            </p>
          </div>
          <Icon name="Shield" size={20} className="text-accent" />
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground mb-1">
              Configuration Audit Trail
            </h3>
            <p className="text-sm text-muted-foreground">
              Track all configuration changes and administrative actions
            </p>
          </div>
          <Icon name="Shield" size={20} className="text-accent" />
        </div>
        <div className="flex items-center gap-2 text-error mb-2">
          <Icon name="AlertCircle" size={20} />
          <p className="text-sm font-medium">Error loading audit trail</p>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <button
          onClick={loadAuditTrail}
          className="text-sm text-primary hover:text-primary/80 transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground mb-1">
            Configuration Audit Trail
          </h3>
          <p className="text-sm text-muted-foreground">
            Track all configuration changes and administrative actions
          </p>
        </div>
        <Icon name="Shield" size={20} className="text-accent" />
      </div>
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {auditLog?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No audit entries yet</p>
          </div>
        ) : (
          auditLog?.map((entry) => (
            <div
              key={entry?.id}
              className="flex items-start gap-3 p-3 rounded-md hover:bg-muted/50 transition-colors duration-150"
            >
              <div className={`flex-shrink-0 ${getActionColor(entry?.action)}`}>
                <Icon name={getActionIcon(entry?.action)} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-card-foreground">
                  {entry?.description}
                </p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-muted-foreground">
                    {formatTimestamp(entry?.timestamp)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    by {entry?.user}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConfigurationAudit;